import { BILLING_FLOW_CONFIG } from '@/config/app.config';
import { useAuth } from '@/contexts/auth-context';
import { useSubscriptionQuery } from '@/hooks/queries/use-subscription-query';
import { usePusher } from '@/hooks/usePusher';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingState } from './_components/LoadingState';
import { SuccessState } from './_components/SuccessState';
import { WelcomeLayout } from './_components/WelcomeLayout';

interface SubscriptionActivatedEvent {
  subscription: {
    id: string;
    status: string;
    planName: string;
  };
}

const FALLBACK_TIMEOUT = BILLING_FLOW_CONFIG.WEBHOOK_ACTIVATION_TIMEOUT_MS;

const ShopifyWelcomePage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [isActivating, setIsActivating] = useState(true);

  const success = searchParams.get('success') === 'true';

  // Check if subscription is already active
  const {
    data: subscription,
    error: subscriptionError,
    isLoading: isLoadingSubscription,
  } = useSubscriptionQuery();

  // Refetch subscription and redirect to dashboard
  const redirectToDashboard = useCallback(async () => {
    // Always refetch subscription data before redirecting to ensure fresh data
    await queryClient.refetchQueries({ queryKey: ['subscription'] });
    navigate('/dashboard');
  }, [queryClient, navigate]);

  // Handle subscription activation via Pusher
  const handleSubscriptionActivated = useCallback(
    (_data: SubscriptionActivatedEvent) => {
      // Invalidate cache to mark it as stale
      // The refetch will happen in redirectToDashboard before navigation
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      setIsActivating(false);
    },
    [queryClient]
  );

  // Subscribe to Pusher channel
  const brandId = user?.currentBrandId;
  const shouldSubscribe = !!brandId && isActivating;

  usePusher({
    channelName: brandId ? `private-brand-${brandId}` : '',
    eventName: 'subscription-activated',
    callback: handleSubscriptionActivated,
    enabled: shouldSubscribe,
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Handle subscription query errors
  useEffect(() => {
    if (subscriptionError) {
      console.error('[Welcome] Failed to check subscription status:', subscriptionError);
      // Still redirect to dashboard on error after timeout
      // User will see subscription status on dashboard
    }
  }, [subscriptionError]);

  // Handle success or active subscription
  useEffect(() => {
    if (!isAuthenticated) return;

    if (success || subscription?.status === 'active') {
      setIsActivating(false);
    }
  }, [success, subscription, isAuthenticated]);

  // Fallback timeout for activation
  useEffect(() => {
    if (!isActivating) return;

    const timer = setTimeout(() => {
      redirectToDashboard();
    }, FALLBACK_TIMEOUT);
    return () => clearTimeout(timer);
  }, [isActivating, redirectToDashboard]);

  // Determine what to show
  const showSuccess = success || (!isActivating && subscription?.status === 'active');
  const showLoading = !success && !showSuccess && (isActivating || isLoadingSubscription);

  return (
    <WelcomeLayout variant={showSuccess ? 'success' : 'loading'}>
      {showSuccess && <SuccessState onRedirect={redirectToDashboard} />}
      {showLoading && <LoadingState />}
    </WelcomeLayout>
  );
};

export default ShopifyWelcomePage;
